/**
 * OTP Service
 * Handles OTP generation and validation
 * 
 * Location: ${TOP-SRC}/src/components/api/auth/otpService.js
 */

import { API_ENDPOINTS, apiCall } from '../../config/apiConfig';

/**
 * Request OTP for mobile number
 * @param {string} mobileNumber - 10-digit mobile number
 * @returns {Promise<object>} Response with success status
 */
export const requestOTP = async (mobileNumber) => {
  try {
    console.log('📱 Requesting OTP for:', mobileNumber);

    // Validate mobile number
    if (!mobileNumber || mobileNumber.length !== 10) {
      return {
        success: false,
        message: 'Please enter a valid 10-digit mobile number',
      };
    }

    const result = await apiCall(API_ENDPOINTS.OTP.GENERATE, {
      method: 'POST',
      body: JSON.stringify({ mobile: mobileNumber }),
    });

    if (result.success) {
      console.log('✅ OTP sent successfully');
      return {
        success: true,
        message: result.data.message || 'OTP sent successfully',
        otp: result.data.otp, // Only in mock mode for testing
      };
    } else {
      console.error('❌ Failed to send OTP:', result.message);
      return {
        success: false,
        message: result.message || 'Failed to send OTP',
      };
    }
  } catch (error) {
    console.error('❌ Error requesting OTP:', error);
    return {
      success: false,
      message: 'Server error. Please try again.',
    };
  }
};

/**
 * Validate OTP
 * @param {string} mobileNumber - Mobile number
 * @param {string} otp - 6-digit OTP
 * @returns {Promise<object>} Response with validation status
 */
export const validateOTP = async (mobileNumber, otp) => {
  try {
    console.log('🔐 Validating OTP for:', mobileNumber);

    // Validate input
    if (!otp || otp.length !== 6) {
      return {
        success: false,
        message: 'Please enter a valid 6-digit OTP',
      };
    }

    const result = await apiCall(API_ENDPOINTS.OTP.VALIDATE, {
      method: 'POST',
      body: JSON.stringify({ 
        mobile: mobileNumber, 
        otp: otp 
      }),
    });

    if (result.success) {
      console.log('✅ OTP verified successfully');
      return {
        success: true,
        message: result.data.message || 'OTP verified successfully',
        deviceId: result.data.deviceId || result.data.DeviceID,
      };
    } else {
      console.error('❌ Invalid OTP');
      return {
        success: false,
        message: result.data?.message || 'Invalid OTP. Please try again.',
      };
    }
  } catch (error) {
    console.error('❌ Error validating OTP:', error);
    return {
      success: false,
      message: 'Server error. Please try again.',
    };
  }
};

/**
 * Resend OTP
 * @param {string} mobileNumber - Mobile number
 * @returns {Promise<object>} Response with success status
 */
export const resendOTP = async (mobileNumber) => {
  console.log('🔄 Resending OTP');
  return await requestOTP(mobileNumber);
};
